import type { ReactNode } from 'react'
import FichaHeader from '@/frontend/components/DigitalWorkers/perfil/FichaHeader'
import WorkerPortalNav from '@/frontend/layouts/worker-first/WorkerPortalNav'
import type { DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { cn } from '@/lib/utils'

interface Props {
  worker: DigitalWorkerDetail
  // Authoritative section key on the worker Show page; the agent-scoped org
  // pages omit it and let WorkerPortalNav decide the active group by URL.
  section?: string
  // Page-specific chrome that must share the canonical header width and order.
  notice?: ReactNode
  // Viewport-owned sections such as Chat and Memory consume the remaining
  // dashboard height after the shared worker header instead of estimating
  // that space with a fixed viewport subtraction.
  fillAvailableHeight?: boolean
  children: ReactNode
}

/**
 * Moldura única do portal do agente (Iteração 2): FichaHeader (identidade —
 * avatar, "● Ativa", cargo, canais) + WorkerPortalNav (somente as irmãs
 * profundas do grupo ativo, quando existem) e, abaixo, o conteúdo. É a MESMA
 * hierarquia que a Show montava inline; extraída para cá para que a Show e as
 * cinco páginas filtradas do portal (processos/publicações/integrações/
 * credenciais/auditoria) compartilhem uma fonte só — sem a identidade do
 * agente sumir ao entrar nessas abas (era o bug).
 *
 * O espaçamento externo (px/py) fica com quem monta. O chrome compartilhado
 * mantém a largura canônica `max-w-5xl`, exceto na Memória: como essa seção
 * ocupa a largura disponível, a identidade acompanha o mesmo eixo esquerdo do
 * explorador. O conteúdo fica fora dessa moldura para que o Chat possa usar a
 * largura fluida entregue pelo layout sem fazer a identidade do agente saltar
 * horizontalmente entre seções.
 */
export default function WorkerPortalShell({
  worker,
  section,
  notice,
  fillAvailableHeight = false,
  children,
}: Props) {
  const isReady = worker.provisioning_status === 'ready'
  const wideIdentityHeader = section === 'memoria'
  const compactChatHeader = section === 'chat'

  return (
    <div
      className={`min-w-0 max-w-full ${
        fillAvailableHeight
          ? `flex min-h-0 flex-1 flex-col ${compactChatHeader ? 'gap-0' : 'gap-6'}`
          : 'space-y-6'
      }`}
    >
      {compactChatHeader ? notice : (
        <div className={cn(
          'mx-auto w-full min-w-0 shrink-0 space-y-6 transition-[max-width] duration-500 ease-out motion-reduce:transition-none',
          wideIdentityHeader ? 'max-w-[100%]' : 'max-w-5xl',
        )}>
          {notice}
          <FichaHeader worker={worker} />
          <WorkerPortalNav workerId={worker.id} current={section} workerReady={isReady} />
        </div>
      )}
      {children}
    </div>
  )
}
